import { decode } from '@here/flexpolyline';
import type { Position } from '@rio-cloud/rio-uikit/mapTypes';
import { z } from 'zod';

import { type RoutingSettings, routingSettingsSchema } from './routingSettings.ts';
import type { TourStop } from './TourStop.ts';
import { getTourStopMapItems } from './tourStopsMap.utils.ts';

export type RoutingMode = 'truck' | 'ev';
export type ChargingStop = {
    id: string;
    position: Position;
    name?: string;
    duration: number;
    arrivalCharge: number;
    targetCharge: number;
    afterStopIndex?: number;
};
export const EV_DEMO_PROFILE = {
    maxCharge: 300,
    initialCharge: 180,
    minChargeAtChargingStation: 30,
    minChargeAtDestination: 30,
    maxChargeAfterChargingStation: 270,
    connectorTypes: 'iec62196Type2Combo',
    freeFlowSpeedTable: '10,1,50,1,80,1.3,100,1.6,130,2',
    chargingCurve: '0,200,240,200,270,100,300,50',
    chargingSetupDuration: 300,
    makeReachable: true,
};
export type SmartRouteResult = {
    positions: Position[];
    distance: number;
    duration: number;
    hasWarnings: boolean;
    chargingStops?: ChargingStop[];
    vehicleCost?: number;
    tollCost?: number;
    chargingDuration?: number;
};

const noticeSchema = z.object({ severity: z.string(), code: z.string().optional() });
const chargeSchema = z.number().finite().min(0).max(2000);
const evSectionSchema = z.object({
    arrival: z.object({
        charge: chargeSchema,
        place: z.object({
            location: z.object({
                lat: z.number().finite().min(-90).max(90),
                lng: z.number().finite().min(-180).max(180),
            }),
            name: z.string().optional(),
            type: z.string().optional(),
        }),
    }),
    postActions: z
        .array(
            z.object({
                action: z.string(),
                duration: z.number().finite().nonnegative(),
                arrivalCharge: chargeSchema.optional(),
                targetCharge: chargeSchema.optional(),
            })
        )
        .optional(),
});
const responseSchema = z.object({
    routes: z
        .array(
            z.object({
                notices: z.array(noticeSchema).optional(),
                sections: z
                    .array(
                        z.object({
                            polyline: z.string().min(1),
                            travelSummary: z.object({
                                length: z.number().finite().nonnegative(),
                                duration: z.number().finite().nonnegative(),
                            }),
                            notices: z.array(noticeSchema).optional(),
                            summary: z
                                .object({
                                    tolls: z
                                        .object({
                                            total: z
                                                .object({
                                                    value: z.number().finite().nonnegative(),
                                                    currency: z.string(),
                                                })
                                                .optional(),
                                        })
                                        .optional(),
                                })
                                .optional(),
                        })
                    )
                    .min(1),
            })
        )
        .min(1),
});

export const canCalculateSmartRoute = (stops: TourStop[]) =>
    stops.length >= 2 && getTourStopMapItems(stops).length === stops.length;

export const createSmartRouteUrl = (
    stops: TourStop[],
    apiKey: string,
    mode: RoutingMode = 'truck',
    settings?: RoutingSettings
) => {
    if (!apiKey || !canCalculateSmartRoute(stops)) {
        throw new Error('Invalid routing input');
    }
    const positions = getTourStopMapItems(stops).map(item => `${item.position.lat},${item.position.lng}`);
    const params = new URLSearchParams({
        apiKey,
        transportMode: 'truck',
        routingMode: 'fast',
        departureTime: 'any',
        return: 'polyline,travelSummary',
        origin: positions[0],
        destination: positions[positions.length - 1],
    });
    for (const via of positions.slice(1, -1)) {
        params.append('via', via);
    }
    if (mode === 'ev') {
        params.set('return', 'polyline,travelSummary,summary,actions');
        for (const [name, value] of Object.entries(EV_DEMO_PROFILE)) {
            params.set(`ev[${name}]`, String(value));
        }
    }
    if (settings) {
        routingSettingsSchema.parse(settings);
        params.set(
            'return',
            mode === 'ev' ? 'polyline,travelSummary,summary,actions,tolls' : 'polyline,travelSummary,summary,tolls'
        );
        params.set('currency', 'EUR');
        params.set('tolls[summaries]', 'total');
        const vehicleParams = {
            height: Math.round(settings.height_m * 100),
            width: Math.round(settings.width_m * 100),
            length: Math.round(settings.length_m * 100),
            grossWeight: settings.gross_weight_kg,
            currentWeight: settings.current_weight_kg,
            weightPerAxle: settings.axle_weight_kg,
            axleCount: settings.vehicle_axles + settings.trailer_axles,
            trailerCount: settings.trailer_axles > 0 ? 1 : 0,
            trailerAxleCount: settings.trailer_axles,
            type: settings.trailer_axles > 0 ? 'Tractor' : 'StraightTruck',
        };
        for (const [name, value] of Object.entries(vehicleParams)) {
            params.set(`vehicle[${name}]`, String(value));
        }
        if (settings.tunnel_category !== 'none') {
            params.set('vehicle[tunnelCategory]', settings.tunnel_category);
        }
        if (settings.hazardous_goods !== 'none') {
            params.set('vehicle[shippedHazardousGoods]', settings.hazardous_goods);
        }
        params.set('tolls[emissionType]', settings.emission_type);
        if (mode === 'ev') {
            const battery = settings.battery_kwh;
            const power = settings.charging_power_kw;
            const consumption = settings.consumption_kwh_per_km;
            const profile = {
                maxCharge: battery,
                initialCharge: (battery * settings.initial_charge_percent) / 100,
                minChargeAtChargingStation: (battery * settings.reserve_percent) / 100,
                minChargeAtDestination: (battery * settings.reserve_percent) / 100,
                maxChargeAfterChargingStation: (battery * settings.target_charge_percent) / 100,
                freeFlowSpeedTable: `10,${consumption},50,${consumption},80,${consumption},100,${consumption},130,${consumption}`,
                chargingCurve: `0,${power},${battery * 0.8},${power},${battery * 0.9},${power / 2},${battery},${power / 4}`,
            };
            for (const [name, value] of Object.entries(profile)) {
                params.set(`ev[${name}]`, String(value));
            }
        }
    }
    return `https://router.hereapi.com/v8/routes?${params}`;
};

export const parseSmartRoute = (
    data: unknown,
    mode: RoutingMode = 'truck',
    settings?: RoutingSettings
): SmartRouteResult => {
    const route = responseSchema.parse(data).routes[0];
    const notices = [...(route.notices ?? []), ...route.sections.flatMap(section => section.notices ?? [])];
    if (notices.some(notice => notice.severity === 'critical')) {
        throw new Error('Route has critical notices');
    }
    const positions = route.sections.flatMap(section => {
        const decoded = decode(section.polyline).polyline.map(([lat, lng]: number[]) => ({ lat, lng }));
        if (
            decoded.length < 2 ||
            decoded.some(
                ({ lat, lng }: Position) =>
                    !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180
            )
        ) {
            throw new Error('Invalid route geometry');
        }
        return decoded;
    });
    let chargingStops: ChargingStop[] | undefined;
    let chargingDuration: number | undefined;
    if (mode === 'ev') {
        // Validate the original sections: the base schema intentionally only retains shared routing fields.
        const evRoute = z
            .object({ routes: z.array(z.object({ sections: z.array(evSectionSchema).min(1) })).min(1) })
            .parse(data).routes[0];
        if (
            notices.some(notice => notice.code === 'outOfCharge') ||
            evRoute.sections.some(
                section =>
                    section.arrival.charge > (settings?.battery_kwh ?? EV_DEMO_PROFILE.maxCharge) ||
                    section.arrival.charge <
                        (settings
                            ? (settings.battery_kwh * settings.reserve_percent) / 100
                            : EV_DEMO_PROFILE.minChargeAtDestination)
            )
        ) {
            throw new Error('EV route reserve is not reachable');
        }
        chargingDuration = evRoute.sections.reduce(
            (sum, section) =>
                sum +
                (section.postActions ?? [])
                    .filter(action => action.action === 'charging' || action.action === 'chargingSetup')
                    .reduce((duration, action) => duration + action.duration, 0),
            0
        );
        let waypointIndex = 0;
        chargingStops = evRoute.sections.flatMap((section, sectionIndex) => {
            const actions = section.postActions ?? [];
            const afterStopIndex = waypointIndex;
            if (!actions.some(action => action.action === 'charging')) {
                waypointIndex++;
            }
            return actions
                .filter(action => action.action === 'charging')
                .map((action, actionIndex) => {
                    const arrivalCharge = chargeSchema.parse(action.arrivalCharge);
                    const targetCharge = chargeSchema.parse(action.targetCharge);
                    if (
                        targetCharge < arrivalCharge ||
                        targetCharge > (settings?.battery_kwh ?? EV_DEMO_PROFILE.maxCharge)
                    ) {
                        throw new Error('Invalid charging action');
                    }
                    return {
                        id: `charging-${sectionIndex}-${actionIndex}`,
                        position: section.arrival.place.location,
                        name: section.arrival.place.name,
                        afterStopIndex,
                        duration:
                            action.duration +
                            actions
                                .filter(item => item.action === 'chargingSetup')
                                .reduce((sum, item) => sum + item.duration, 0),
                        arrivalCharge,
                        targetCharge,
                    };
                });
        });
    }
    return {
        positions,
        distance: route.sections.reduce((total, section) => total + section.travelSummary.length, 0),
        duration: route.sections.reduce((total, section) => total + section.travelSummary.duration, 0),
        hasWarnings: notices.length > 0,
        ...(settings
            ? {
                  vehicleCost:
                      (route.sections.reduce((sum, section) => sum + section.travelSummary.length, 0) / 1000) *
                      settings.cost_per_km,
                  tollCost:
                      !notices.some(notice => /tollsData|currencyUnsupported/.test(notice.code ?? '')) &&
                      route.sections.every(section => section.summary?.tolls?.total?.currency === 'EUR')
                          ? route.sections.reduce(
                                (sum, section) => sum + (section.summary?.tolls?.total?.value ?? 0),
                                0
                            )
                          : undefined,
              }
            : {}),
        ...(chargingStops ? { chargingStops, chargingDuration } : {}),
    };
};

export const fetchSmartRoute = async (
    stops: TourStop[],
    apiKey: string,
    signal: AbortSignal,
    mode: RoutingMode = 'truck',
    settings?: RoutingSettings
) => {
    const response = await fetch(createSmartRouteUrl(stops, apiKey, mode, settings), { signal, credentials: 'omit' });
    if (!response.ok) {
        throw new Error('Routing request failed');
    }
    return response.json() as Promise<unknown>;
};
